import React, { useState, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { theme } from 'src/constants/theme';
import { Button, Input, Checkbox, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { Classification } from '../slices/classification';
import { DonorName, ModusOperandi } from 'src/slices/donor';
import { Departments } from '../constants/departments';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';

const { Search } = Input;

export type SearchFiltersParameters = {
  name?: string;
  tags?: string[];
  region?: string[];
  department?: string[];
  activityDomains?: string[];
  activityZones?: string[];
  sustainableDevelopmentGoals?: string[];
  targetPopulations?: string[];
  modusOperandi?: ModusOperandi[];
};

interface Props {
  classification: Classification;
  suggestions: DonorName[] | null;
  searchParameters: SearchFiltersParameters;
  onChangeName?: (value: string) => void;
  suggestionsLoading?: boolean;
  setSearchFilters: React.Dispatch<
    React.SetStateAction<SearchFiltersParameters>
  >;
  isNgo?: boolean;
  isAutoComplete?: boolean;
  classificationsFilter?: any;
  isFromProject?: boolean;
  callForTender?: any;
  financingNeed?: any;
  dropdownFilterVisible?: boolean;
  setDropdownFilterVisible?: (visible: boolean) => void;
  onSelectAlgorithm?: (algorithm: string) => void;
}

const useStyles = createUseStyles({
  container: {
    width: '100%',
    padding: '20px 0',
  },
  searchSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '20px',
  },
  searchBar: {
    '& .ant-input-affix-wrapper': {
      borderTopLeftRadius: '5px !important',
      borderBottomLeftRadius: '5px !important',
      width: '450px',
      height: '48px',
      borderColor: theme.palette.primary,
      paddingLeft: '25px',
    },
    '& .ant-input': {
      height: '100%',
    },
    '& .ant-btn': {
      height: '48px',
      width: '57.58px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.primary,
      border: '1px solid #5050F6',
      borderTopRightRadius: '5px !important',
      borderBottomRightRadius: '5px !important',
    },
    '& .anticon-search': {
      color: 'white',
      fontSize: '25px',
    },
  },
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '15px',
  },
  filterItem: {
    position: 'relative',
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 15px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    gap: '8px',
  },
  filterButtonActive: {
    fontWeight: 'bold',
  },
  filterActive: {
    backgroundColor: '#5078E7',
    borderColor: '#1D4ED8',
    color: 'white',
  },
  resetButton: {
    height: '40px',
    borderRadius: '40px',
    color: theme.palette.primary,
    fontWeight: '600',
    border: '1px solid #5050F6',
    '&:hover': {
      color: theme.palette.primary,
      border: '1px solid #5050F6',
    },
  },
  filtersPanel: {
    position: 'absolute',
    top: '45px',
    left: 0,
    width: '300px',
    maxHeight: '400px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #DFDFDF',
    boxShadow: '0px 4px 8px 0px #00000014',
    zIndex: 1000,
    padding: '15px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  filterTitle: {
    color: '#71717A !important',
    fontWeight: '400',
    fontSize: '13px',
    lineHeight: '13px',
    letterSpacing: '0%',
  },
  resetLink: {
    color: '#71717A !important',
    cursor: 'pointer',
    fontWeight: '400',
    fontSize: '11px',
    lineHeight: '12px',
    letterSpacing: '2%',
  },
  checkboxList: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: '280px',
    padding: '0 5px',
    '&::-webkit-scrollbar': {
      width: '5px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  },
  checkboxGroup: {
    width: '100%',
    '& .ant-checkbox-wrapper': {
      margin: '10px 0',
      display: 'flex',
      alignItems: 'center',
    },
    '& .ant-checkbox': {
      transform: 'scale(1.1)',
      marginRight: '5px',
      marginBottom: '4px',
    },
    '& .ant-checkbox-inner': {
      width: '20px',
      height: '20px',
      borderRadius: '6px',
      border: `1px solid ${theme.palette.grey}`,
    },
    '& .ant-checkbox-checked .ant-checkbox-inner': {
      backgroundColor: theme.palette.primary,
      borderColor: theme.palette.primary,
    },
    '& .ant-checkbox-checked .ant-checkbox-inner::after': {
      width: '6px',
      height: '12px',
      borderWidth: '2px',
    },
  },
  searchInput: {
    marginBottom: '10px',
    '& .ant-input-affix-wrapper': {
      padding: '4px 11px',
      borderRadius: '8px',
    },
    '& .anticon-search': {
      fontSize: '20px',
      marginRight: '7px',
    },
  },
  badge: {
    marginLeft: '8px',
    backgroundColor: '#1D4ED8',
    display: 'inline-block',
    height: '20px',
    width: '20px',
    borderRadius: '12px',
    fontFamily: 'PP Mori',
    fontWeight: '400',
    fontSize: '14px',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  select: {
    padding: '20px',
    '& .ant-select': {
      height: '36px',
      fontSize: '14px',
      '&:not(.ant-select-disabled):hover, &:focus, &:active': {
        '& .ant-select-selector': {
          border: 'none !important',
          boxShadow: 'none !important',
        },
      },
      '& .ant-select-selector': {
        height: '100% !important',
        padding: '8px 16px',
        borderRadius: '8px',
        border: 'none !important',
        boxShadow: 'none !important',
        '&:hover, &:focus, &:active, &.ant-select-focused': {
          border: 'none !important',
          boxShadow: 'none !important',
        },
        '& .ant-select-selection-item': {
          lineHeight: '16px',
          fontSize: '14px',
        },
      },
      '& .ant-select-arrow': {
        fontSize: '14px',
        color: 'black',
      },
      '&.ant-select-disabled .ant-select-selector': {
        backgroundColor: 'transparent',
      },
    },
  },
});

export const DatabaseSearchBar: React.FC<Props> = ({
  classification,
  searchParameters,
  onChangeName,
  setSearchFilters,
  isNgo,
  onSelectAlgorithm,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [regionsFilterVisible, setRegionsFilterVisible] = useState(false);
  const [domainsFilterVisible, setDomainsFilterVisible] = useState(false);
  const [odsFilterVisible, setOdsFilterVisible] = useState(false);
  const [tagsFilterVisible, setTagsFilterVisible] = useState(false);
  const [targetPopulationsFilterVisible, setTargetPopulationsFilterVisible] =
    useState(false);
  const [activityZonesFilterVisible, setActivityZonesFilterVisible] =
    useState(false);
  const [modusOperandiFilterVisible, setModusOperandiFilterVisible] =
    useState(false);
  const [regionsSearchTerm, setRegionsSearchTerm] = useState('');
  const [domainsSearchTerm, setDomainsSearchTerm] = useState('');
  const [odsSearchTerm, setOdsSearchTerm] = useState('');
  const [tagsSearchTerm, setTagsSearchTerm] = useState('');
  const [targetPopulationsSearchTerm, setTargetPopulationsSearchTerm] =
    useState('');
  const [activityZonesSearchTerm, setActivityZonesSearchTerm] = useState('');
  const [modusOperandiSearchTerm, setModusOperandiSearchTerm] = useState('');
  const regionsDropdownRef = useRef<HTMLDivElement>(null);
  const domainsDropdownRef = useRef<HTMLDivElement>(null);
  const odsDropdownRef = useRef<HTMLDivElement>(null);
  const tagsDropdownRef = useRef<HTMLDivElement>(null);
  const targetPopulationsDropdownRef = useRef<HTMLDivElement>(null);
  const activityZonesDropdownRef = useRef<HTMLDivElement>(null);
  const modusOperandiDropdownRef = useRef<HTMLDivElement>(null);

  const [tempFilters, setTempFilters] = useState<SearchFiltersParameters>({
    ...searchParameters,
  });

  const closeAllDropdowns = () => {
    setRegionsFilterVisible(false);
    setDomainsFilterVisible(false);
    setOdsFilterVisible(false);
    setTagsFilterVisible(false);
    setTargetPopulationsFilterVisible(false);
    setActivityZonesFilterVisible(false);
    setModusOperandiFilterVisible(false);
  };

  useEffect(() => {
    setTempFilters({ ...searchParameters });
  }, [searchParameters]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        regionsDropdownRef.current &&
        !regionsDropdownRef.current.contains(event.target as Node)
      ) {
        setRegionsFilterVisible(false);
      }

      if (
        domainsDropdownRef.current &&
        !domainsDropdownRef.current.contains(event.target as Node)
      ) {
        setDomainsFilterVisible(false);
      }

      if (
        odsDropdownRef.current &&
        !odsDropdownRef.current.contains(event.target as Node)
      ) {
        setOdsFilterVisible(false);
      }

      if (
        tagsDropdownRef.current &&
        !tagsDropdownRef.current.contains(event.target as Node)
      ) {
        setTagsFilterVisible(false);
      }

      if (
        targetPopulationsDropdownRef.current &&
        !targetPopulationsDropdownRef.current.contains(event.target as Node)
      ) {
        setTargetPopulationsFilterVisible(false);
      }

      if (
        activityZonesDropdownRef.current &&
        !activityZonesDropdownRef.current.contains(event.target as Node)
      ) {
        setActivityZonesFilterVisible(false);
      }

      if (
        modusOperandiDropdownRef.current &&
        !modusOperandiDropdownRef.current.contains(event.target as Node)
      ) {
        setModusOperandiFilterVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (value: string) => {
    if (onChangeName) {
      onChangeName(value);
    }
    setSearchFilters((prev) => ({ ...prev, name: value }));
  };

  const handleFilterChange = (
    field: keyof SearchFiltersParameters,
    value: string[],
  ) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    const resetValues: SearchFiltersParameters = {
      name: searchParameters.name,
      tags: [],
      region: [],
      department: [],
      activityDomains: [],
      activityZones: [],
      sustainableDevelopmentGoals: [],
      targetPopulations: [],
      modusOperandi: [],
    };
    setTempFilters(resetValues);
    setSearchFilters(resetValues);

    closeAllDropdowns();
  };

  const resetSpecificFilter = (filterType: string) => {
    switch (filterType) {
      case 'regions':
        setTempFilters((prev) => ({ ...prev, region: [] }));
        break;
      case 'domains':
        setTempFilters((prev) => ({ ...prev, activityDomains: [] }));
        break;
      case 'ods':
        setTempFilters((prev) => ({
          ...prev,
          sustainableDevelopmentGoals: [],
        }));
        break;
      case 'tags':
        setTempFilters((prev) => ({ ...prev, tags: [] }));
        break;
      case 'targetPopulations':
        setTempFilters((prev) => ({ ...prev, targetPopulations: [] }));
        break;
      case 'activityZones':
        setTempFilters((prev) => ({ ...prev, activityZones: [] }));
        break;
      case 'modusOperandi':
        setTempFilters((prev) => ({ ...prev, modusOperandi: [] }));
        break;
    }
  };

  const renderRegionsFilterItems = () => {
    const regions = Departments.reduce((prev, curr) => {
      if (!prev.includes(curr.regionName)) {
        prev.push(curr.regionName);
      }
      return prev;
    }, [] as string[]);

    return regions
      .filter((region) =>
        region.toLowerCase().includes(regionsSearchTerm.toLowerCase()),
      )
      .map((region) => (
        <div key={region}>
          <Checkbox
            checked={tempFilters.region?.includes(region)}
            onChange={(e) => {
              if (e.target.checked) {
                handleFilterChange('region', [
                  ...(tempFilters.region || []),
                  region,
                ]);
              } else {
                handleFilterChange(
                  'region',
                  tempFilters.region?.filter((r) => r !== region) || [],
                );
              }
            }}
          >
            {region}
          </Checkbox>
        </div>
      ));
  };

  const renderDomainsFilterItems = () => {
    return classification.activityDomains
      .filter((domain) =>
        t(`activityDomains:${domain.name}`)
          .toLowerCase()
          .includes(domainsSearchTerm.toLowerCase()),
      )
      .map((domain) => (
        <div key={domain._id}>
          <Checkbox
            checked={tempFilters.activityDomains?.includes(domain._id)}
            onChange={(e) => {
              if (e.target.checked) {
                handleFilterChange('activityDomains', [
                  ...(tempFilters.activityDomains || []),
                  domain._id,
                ]);
              } else {
                handleFilterChange(
                  'activityDomains',
                  tempFilters.activityDomains?.filter(
                    (id) => id !== domain._id,
                  ) || [],
                );
              }
            }}
          >
            {t(`activityDomains:${domain.name}`)}
          </Checkbox>
        </div>
      ));
  };

  const renderOdsFilterItems = () => {
    return classification.sustainableDevelopmentGoals
      .filter((goal) => {
        const goalText = `${t(`sustainableDevelopmentGoals:${goal.name}`)}`;
        return goalText.toLowerCase().includes(odsSearchTerm.toLowerCase());
      })
      .sort((a, b) => {
        if (a.number && b.number) {
          return parseInt(a.number) - parseInt(b.number);
        }
        return 0;
      })
      .map((goal) => (
        <div key={goal._id}>
          <Checkbox
            checked={tempFilters.sustainableDevelopmentGoals?.includes(
              goal._id,
            )}
            onChange={(e) => {
              if (e.target.checked) {
                handleFilterChange('sustainableDevelopmentGoals', [
                  ...(tempFilters.sustainableDevelopmentGoals || []),
                  goal._id,
                ]);
              } else {
                handleFilterChange(
                  'sustainableDevelopmentGoals',
                  tempFilters.sustainableDevelopmentGoals?.filter(
                    (id) => id !== goal._id,
                  ) || [],
                );
              }
            }}
          >
            {`${t(`sustainableDevelopmentGoals:${goal.name}`)}`}
          </Checkbox>
        </div>
      ));
  };

  const renderTagsFilterItems = () => {
    return classification.tags
      .filter((tag) =>
        t(`tags:${tag.name}`)
          .toLowerCase()
          .includes(tagsSearchTerm.toLowerCase()),
      )
      .map((tag) => (
        <div key={tag._id}>
          <Checkbox
            checked={tempFilters.tags?.includes(tag._id)}
            onChange={(e) => {
              if (e.target.checked) {
                handleFilterChange('tags', [
                  ...(tempFilters.tags || []),
                  tag._id,
                ]);
              } else {
                handleFilterChange(
                  'tags',
                  tempFilters.tags?.filter((id) => id !== tag._id) || [],
                );
              }
            }}
          >
            {t(`tags:${tag.name}`)}
          </Checkbox>
        </div>
      ));
  };

  const renderTargetPopulationsFilterItems = () => {
    return classification.targetPopulations
      .filter((population) =>
        t(`targetPopulations:${population.name}`)
          .toLowerCase()
          .includes(targetPopulationsSearchTerm.toLowerCase()),
      )
      .map((population) => (
        <div key={population._id}>
          <Checkbox
            checked={tempFilters.targetPopulations?.includes(population._id)}
            onChange={(e) => {
              if (e.target.checked) {
                handleFilterChange('targetPopulations', [
                  ...(tempFilters.targetPopulations || []),
                  population._id,
                ]);
              } else {
                handleFilterChange(
                  'targetPopulations',
                  tempFilters.targetPopulations?.filter(
                    (id) => id !== population._id,
                  ) || [],
                );
              }
            }}
          >
            {t(`targetPopulations:${population.name}`)}
          </Checkbox>
        </div>
      ));
  };

  const renderActivityZonesFilterItems = () => {
    return classification.activityZones
      .filter((zone) =>
        t(`activityZones:${zone.name}`)
          .toLowerCase()
          .includes(activityZonesSearchTerm.toLowerCase()),
      )
      .map((zone) => (
        <div key={zone._id}>
          <Checkbox
            checked={tempFilters.activityZones?.includes(zone._id)}
            onChange={(e) => {
              if (e.target.checked) {
                handleFilterChange('activityZones', [
                  ...(tempFilters.activityZones || []),
                  zone._id,
                ]);
              } else {
                handleFilterChange(
                  'activityZones',
                  tempFilters.activityZones?.filter((id) => id !== zone._id) ||
                    [],
                );
              }
            }}
          >
            {t(`activityZones:${zone.name}`)}
          </Checkbox>
        </div>
      ));
  };

  const renderModusOperandiFilterItems = () => {
    const modusOperandiValues = Object.values(ModusOperandi);
    return modusOperandiValues
      .filter((mode) =>
        mode.toLowerCase().includes(modusOperandiSearchTerm.toLowerCase()),
      )
      .map((mode) => (
        <div key={mode}>
          <Checkbox
            checked={tempFilters.modusOperandi?.includes(mode)}
            onChange={(e) => {
              if (e.target.checked) {
                handleFilterChange('modusOperandi', [
                  ...(tempFilters.modusOperandi || []),
                  mode,
                ]);
              } else {
                handleFilterChange(
                  'modusOperandi',
                  tempFilters.modusOperandi?.filter((m) => m !== mode) || [],
                );
              }
            }}
          >
            {mode}
          </Checkbox>
        </div>
      ));
  };

  const getSelectedFilterCount = (filterType: string) => {
    switch (filterType) {
      case 'regions':
        return searchParameters.region?.length || 0;
      case 'domains':
        return searchParameters.activityDomains?.length || 0;
      case 'ods':
        return searchParameters.sustainableDevelopmentGoals?.length || 0;
      case 'tags':
        return searchParameters.tags?.length || 0;
      case 'targetPopulations':
        return searchParameters.targetPopulations?.length || 0;
      case 'activityZones':
        return searchParameters.activityZones?.length || 0;
      case 'modusOperandi':
        return searchParameters.modusOperandi?.length || 0;
      default:
        return 0;
    }
  };

  const isFilterActive = (filterType: string) => {
    return getSelectedFilterCount(filterType) > 0;
  };

  const getFilterButtonClasses = (filterType: string) => {
    let buttonClasses = classes.filterButton;

    if (isFilterActive(filterType)) {
      buttonClasses +=
        ' ' + classes.filterButtonActive + ' ' + classes.filterActive;
    }

    return buttonClasses;
  };

  const toggleDropdown = (dropdownName: string) => {
    closeAllDropdowns();

    switch (dropdownName) {
      case 'regions':
        setRegionsFilterVisible(!regionsFilterVisible);
        break;
      case 'domains':
        setDomainsFilterVisible(!domainsFilterVisible);
        break;
      case 'ods':
        setOdsFilterVisible(!odsFilterVisible);
        break;
      case 'tags':
        setTagsFilterVisible(!tagsFilterVisible);
        break;
      case 'targetPopulations':
        setTargetPopulationsFilterVisible(!targetPopulationsFilterVisible);
        break;
      case 'activityZones':
        setActivityZonesFilterVisible(!activityZonesFilterVisible);
        break;
      case 'modusOperandi':
        setModusOperandiFilterVisible(!modusOperandiFilterVisible);
        break;
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.searchSection}>
        <div className={classes.searchBar}>
          <Search
            placeholder="Recherchez"
            allowClear
            onSearch={handleSearch}
            value={searchParameters.name}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        {onSelectAlgorithm && (
          <div className={classes.select}>
            <Select
              defaultValue="tagsProximity"
              onChange={(e) => onSelectAlgorithm(e)}
              dropdownMatchSelectWidth={false}
              options={[
                {
                  value: 'tagsProximity',
                  label: 'Proximité par mot-clés',
                },
                {
                  value: 'ngosProximity',
                  label: 'Proximité par porteurs de projets',
                },
                {
                  value: 'donorsProximity',
                  label: 'Proximité par mécènes',
                },
              ]}
            />
          </div>
        )}
        <Button className={classes.resetButton} onClick={resetFilters}>
          Réinitialiser les filtres
        </Button>
      </div>
      <div className={classes.filtersContainer}>
        {/* Tags Filter */}
        <div className={classes.filterItem}>
          <div
            className={getFilterButtonClasses('tags')}
            onClick={() => toggleDropdown('tags')}
          >
            <span>
              Mot clés
              {isFilterActive('tags') && (
                <span className={classes.badge}>
                  {getSelectedFilterCount('tags')}
                </span>
              )}
            </span>
            <DownOutlined />
          </div>

          {tagsFilterVisible && (
            <div className={classes.filtersPanel} ref={tagsDropdownRef}>
              <div className={classes.filterHeader}>
                <div className={classes.filterTitle}>Mot clés</div>
                <a
                  className={classes.resetLink}
                  onClick={() => resetSpecificFilter('tags')}
                >
                  Réinitialiser
                </a>
              </div>
              <div className={classes.searchInput}>
                <Input
                  placeholder="Rechercher"
                  value={tagsSearchTerm}
                  onChange={(e) => setTagsSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </div>
              <div className={classes.checkboxList}>
                <div className={classes.checkboxGroup}>
                  {renderTagsFilterItems()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Target Populations Filter */}
        <div className={classes.filterItem}>
          <div
            className={getFilterButtonClasses('targetPopulations')}
            onClick={() => toggleDropdown('targetPopulations')}
          >
            <span>
              Public cible
              {isFilterActive('targetPopulations') && (
                <span className={classes.badge}>
                  {getSelectedFilterCount('targetPopulations')}
                </span>
              )}
            </span>
            <DownOutlined />
          </div>

          {targetPopulationsFilterVisible && (
            <div
              className={classes.filtersPanel}
              ref={targetPopulationsDropdownRef}
            >
              <div className={classes.filterHeader}>
                <div className={classes.filterTitle}>Public cible</div>
                <a
                  className={classes.resetLink}
                  onClick={() => resetSpecificFilter('targetPopulations')}
                >
                  Réinitialiser
                </a>
              </div>
              <div className={classes.searchInput}>
                <Input
                  placeholder="Rechercher"
                  value={targetPopulationsSearchTerm}
                  onChange={(e) =>
                    setTargetPopulationsSearchTerm(e.target.value)
                  }
                  prefix={<SearchOutlined />}
                />
              </div>
              <div className={classes.checkboxList}>
                <div className={classes.checkboxGroup}>
                  {renderTargetPopulationsFilterItems()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Activity Zones Filter - Only show if isNgo is true */}
        {isNgo && (
          <div className={classes.filterItem}>
            <div
              className={getFilterButtonClasses('activityZones')}
              onClick={() => toggleDropdown('activityZones')}
            >
              <span>
                Zone d&apos;activité
                {isFilterActive('activityZones') && (
                  <span className={classes.badge}>
                    {getSelectedFilterCount('activityZones')}
                  </span>
                )}
              </span>
              <DownOutlined />
            </div>

            {activityZonesFilterVisible && (
              <div
                className={classes.filtersPanel}
                ref={activityZonesDropdownRef}
              >
                <div className={classes.filterHeader}>
                  <div className={classes.filterTitle}>
                    Zone d&apos;activité
                  </div>
                  <a
                    className={classes.resetLink}
                    onClick={() => resetSpecificFilter('activityZones')}
                  >
                    Réinitialiser
                  </a>
                </div>
                <div className={classes.searchInput}>
                  <Input
                    placeholder="Rechercher"
                    value={activityZonesSearchTerm}
                    onChange={(e) => setActivityZonesSearchTerm(e.target.value)}
                    prefix={<SearchOutlined />}
                  />
                </div>
                <div className={classes.checkboxList}>
                  <div className={classes.checkboxGroup}>
                    {renderActivityZonesFilterItems()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Regions Filter */}
        <div className={classes.filterItem}>
          <div
            className={getFilterButtonClasses('regions')}
            onClick={() => toggleDropdown('regions')}
          >
            <span>
              Régions
              {isFilterActive('regions') && (
                <span className={classes.badge}>
                  {getSelectedFilterCount('regions')}
                </span>
              )}
            </span>
            <DownOutlined />
          </div>

          {regionsFilterVisible && (
            <div className={classes.filtersPanel} ref={regionsDropdownRef}>
              <div className={classes.filterHeader}>
                <div className={classes.filterTitle}>Régions</div>
                <a
                  className={classes.resetLink}
                  onClick={() => resetSpecificFilter('regions')}
                >
                  Réinitialiser
                </a>
              </div>
              <div className={classes.searchInput}>
                <Input
                  placeholder="Rechercher"
                  value={regionsSearchTerm}
                  onChange={(e) => setRegionsSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </div>
              <div className={classes.checkboxList}>
                <div className={classes.checkboxGroup}>
                  {renderRegionsFilterItems()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mode Opératoire Filter */}
        <div className={classes.filterItem}>
          <div
            className={getFilterButtonClasses('modusOperandi')}
            onClick={() => toggleDropdown('modusOperandi')}
          >
            <span>
              Mode opératoire
              {isFilterActive('modusOperandi') && (
                <span className={classes.badge}>
                  {getSelectedFilterCount('modusOperandi')}
                </span>
              )}
            </span>
            <DownOutlined />
          </div>

          {modusOperandiFilterVisible && (
            <div
              className={classes.filtersPanel}
              ref={modusOperandiDropdownRef}
            >
              <div className={classes.filterHeader}>
                <div className={classes.filterTitle}>Mode opératoire</div>
                <a
                  className={classes.resetLink}
                  onClick={() => resetSpecificFilter('modusOperandi')}
                >
                  Réinitialiser
                </a>
              </div>
              <div className={classes.searchInput}>
                <Input
                  placeholder="Rechercher"
                  value={modusOperandiSearchTerm}
                  onChange={(e) => setModusOperandiSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </div>
              <div className={classes.checkboxList}>
                <div className={classes.checkboxGroup}>
                  {renderModusOperandiFilterItems()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Domains Filter */}
        <div className={classes.filterItem}>
          <div
            className={getFilterButtonClasses('domains')}
            onClick={() => toggleDropdown('domains')}
          >
            <span>
              Domaine
              {isFilterActive('domains') && (
                <span className={classes.badge}>
                  {getSelectedFilterCount('domains')}
                </span>
              )}
            </span>
            <DownOutlined />
          </div>

          {domainsFilterVisible && (
            <div className={classes.filtersPanel} ref={domainsDropdownRef}>
              <div className={classes.filterHeader}>
                <div className={classes.filterTitle}>Domaine</div>
                <a
                  className={classes.resetLink}
                  onClick={() => resetSpecificFilter('domains')}
                >
                  Réinitialiser
                </a>
              </div>
              <div className={classes.searchInput}>
                <Input
                  placeholder="Rechercher"
                  value={domainsSearchTerm}
                  onChange={(e) => setDomainsSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </div>
              <div className={classes.checkboxList}>
                <div className={classes.checkboxGroup}>
                  {renderDomainsFilterItems()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ODS Filter */}
        <div className={classes.filterItem}>
          <div
            className={getFilterButtonClasses('ods')}
            onClick={() => toggleDropdown('ods')}
          >
            <span>
              O.D.D
              {isFilterActive('ods') && (
                <span className={classes.badge}>
                  {getSelectedFilterCount('ods')}
                </span>
              )}
            </span>
            <DownOutlined />
          </div>

          {odsFilterVisible && (
            <div className={classes.filtersPanel} ref={odsDropdownRef}>
              <div className={classes.filterHeader}>
                <div className={classes.filterTitle}>O.D.D</div>
                <a
                  className={classes.resetLink}
                  onClick={() => resetSpecificFilter('ods')}
                >
                  Réinitialiser
                </a>
              </div>
              <div className={classes.searchInput}>
                <Input
                  placeholder="Rechercher"
                  value={odsSearchTerm}
                  onChange={(e) => setOdsSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </div>
              <div className={classes.checkboxList}>
                <div className={classes.checkboxGroup}>
                  {renderOdsFilterItems()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
